/**
 * @fileoverview SessionViewerContext - Utility Functions
 * 
 * Provides utility functions for determining enabled components based on session state.
 * 
 * @module vtc/contexts/SessionViewerContext/utils
 */

import useSessionStore from '../../stores/session';

/**
 * Get enabled components configuration for a session
 * 
 * Determines which SessionViewer components should be enabled based on:
 * - Whether the session is focused
 * - The session's current location (testing, review, etc.)
 * - User permissions (TODO)
 * 
 * **Component States:**
 * - **Focused Session**: All controls enabled, tabs configured based on location
 * - **Non-Focused Session**: All controls disabled (grid view)
 * 
 * **Location-Based Tab Configuration:**
 * - **testing**: Shows flags, info, identity, communication tabs
 * - **review/waiting**: Shows checkin, info, identity, communication tabs
 * 
 * @param {string} sessionSid - Session identifier
 * @param {string} [defaultTab='flags'] - Default tab to show in SessionDetails (deprecated parameter)
 * @returns {Object} Enabled components configuration
 * @returns {boolean} return.controls - Enable player controls
 * @returns {Object|boolean} return.sessionDetails - SessionDetails panel configuration
 * @returns {string} return.sessionDetails.defaultTab - Default active tab
 * @returns {Array<string|Object>} return.sessionDetails.tabs - Available tabs
 * @returns {boolean} return.chat - Enable chat component
 * @returns {boolean} return.microphone - Enable microphone controls
 * @returns {boolean} return.camera - Enable camera controls
 * @returns {boolean} return.screenShare - Enable screen share
 * @returns {boolean} return.settings - Enable settings panel
 * @returns {boolean} return.timeline - Enable timeline component
 * @returns {Object} return.video - Video source configuration
 * @returns {Array<string>} return.video.sources - Available video sources ('live', 'recorded')
 * @returns {string} return.video.default - Default video source to display
 * 
 * @example
 * // Get enabled components for focused session
 * const enabledComponents = getEnabledComponentsForSession('session_123');
 * // Returns: { controls: true, sessionDetails: { defaultTab: 'flags', tabs: [...] }, ... }
 * 
 * @example
 * // Get enabled components for non-focused session
 * const enabledComponents = getEnabledComponentsForSession('session_456');
 * // Returns: {} (empty object, all disabled)
 */
export function getEnabledComponentsForSession(sessionSid, defaultTab = 'flags') {
    if (!sessionSid) return {};

    const focusedSessionSid = useSessionStore.getState().focusedSessionSid;
    const sessions = useSessionStore.getState().sessions;
    const session = sessions[sessionSid];

    // Only enable components for the focused session
    if (focusedSessionSid !== sessionSid) {
        return {};
    }

    // TODO: Work the user's permissions into this so they don't see something they should not....

    // Configure session details based on location
    const sessionDetails = {};

    // WGU Accommodations demo item (disabled by default)
    // Uncomment to enable:
    // const wguAccommodationsItem = {
    //     id: "accommodations",
    //     title: 'Accommodations',
    //     url: 'https://cdn.smarterservices.com/block-kit-samples/wgu-sample-accomodations.json'
    // };
    
    // Configure tabs based on session location
    if (session.location === 'testing') {
        sessionDetails.defaultTab = 'flags';
        sessionDetails.tabs = ['info', 'identity', 'flags', 'communication', 'incidentReport'];
    } else if (session.location === 'checkout') {
        sessionDetails.defaultTab = 'flags';
        sessionDetails.tabs = ['info', 'identity', 'flags', 'communication', 'incidentReport'];
    } else {
        // review, waiting, or other states
        sessionDetails.defaultTab = 'checkin';
        sessionDetails.defaultCollapsed = false;
        sessionDetails.tabs = ['info', 'identity', 'checkin', 'communication'];
    }

    // Configure video sources based on session location
    const video = {};
    if (session.location === 'checkout') {
        // Checkout sessions should only show recorded video
        video.sources = ['recorded'];
        video.default = 'recorded';
    } else {
        // All other sessions can view live or recorded
        video.sources = ['live', 'recorded'];
        video.default = 'live';
    }

    // Return full enabled components configuration
    return {
        controls: true,
        sessionDetails,
        chat: true,
        microphone: true,
        screenShare: false,
        camera: false,
        settings: true,
        timeline: true,
        video,
    };
}
